/**
 * Short-lived token for the price service, issued by our api
 * (`GET /prices/token`). Cached, renewed about a minute before it expires and
 * refetched when the service says it is no longer valid.
 */

export interface PriceServiceToken {
  token: string;
  /**
   * Unix ms on this device's clock: receive time + the api's `expiresIn`, so a
   * phone clock that is off does not make a fresh token look expired.
   */
  expiresAt: number;
  /** Price service base URL, e.g. https://prices.trigger.fun */
  url: string;
}

interface PriceTokenResponse {
  success?: boolean;
  data?: {
    token?: string;
    /** Unix ms on the api's clock (older api versions send only this). */
    expiresAt?: number;
    /** Seconds the token is valid from issue. */
    expiresIn?: number;
    url?: string;
  };
  error?: string;
}

/** Renew this long before the token expires. */
export const TOKEN_RENEW_BEFORE_MS = 60_000;
/** Renewal and socket rotation timers never fire sooner than this. */
export const MIN_RENEWAL_DELAY_MS = 60_000;

export interface PriceTokenManagerOptions {
  apiBaseUrl: string;
  getJwt: () => string;
  fetchFn?: typeof fetch;
  now?: () => number;
}

export class PriceTokenManager {
  private current: PriceServiceToken | null = null;
  private inflight: Promise<PriceServiceToken> | null = null;
  private readonly fetchFn: typeof fetch;
  private readonly now: () => number;

  constructor(private readonly options: PriceTokenManagerOptions) {
    this.fetchFn = options.fetchFn ?? ((input, init) => fetch(input, init));
    this.now = options.now ?? Date.now;
  }

  /** A token valid for at least another minute. */
  async get(): Promise<PriceServiceToken> {
    if (this.current && !this.needsRenewal(this.current)) {
      return this.current;
    }
    return this.refresh();
  }

  /** Drop the cached token, e.g. after a 401 or an `expired` message. */
  invalidate(rejected?: string): void {
    if (!this.current) return;
    if (rejected && this.current.token !== rejected) return;
    this.current = null;
  }

  /**
   * Milliseconds until the current token should be renewed, never less than
   * MIN_RENEWAL_DELAY_MS so a bad expiry cannot make callers loop.
   */
  msUntilRenewal(): number {
    if (!this.current) return MIN_RENEWAL_DELAY_MS;
    return Math.max(
      MIN_RENEWAL_DELAY_MS,
      this.current.expiresAt - TOKEN_RENEW_BEFORE_MS - this.now()
    );
  }

  private needsRenewal(token: PriceServiceToken): boolean {
    return token.expiresAt - TOKEN_RENEW_BEFORE_MS <= this.now();
  }

  private refresh(): Promise<PriceServiceToken> {
    if (this.inflight) return this.inflight;

    this.inflight = this.request().finally(() => {
      this.inflight = null;
    });
    return this.inflight;
  }

  private async request(): Promise<PriceServiceToken> {
    const jwt = this.options.getJwt();
    const requestedAt = this.now();
    const response = await this.fetchFn(
      `${this.options.apiBaseUrl}/prices/token`,
      {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      }
    );
    if (!response.ok) {
      throw new Error(`Price token request failed with status ${response.status}`);
    }

    const body = (await response.json()) as PriceTokenResponse;
    const data = body?.data;
    if (
      !body?.success ||
      !data ||
      typeof data.token !== "string" ||
      typeof data.url !== "string"
    ) {
      throw new Error(body?.error || "Price token response was malformed");
    }

    let expiresAt: number;
    if (typeof data.expiresIn === "number" && data.expiresIn > 0) {
      // Counted from when the request went out, so it never runs late.
      expiresAt = requestedAt + data.expiresIn * 1000;
    } else if (typeof data.expiresAt === "number") {
      expiresAt = data.expiresAt;
    } else {
      throw new Error(body?.error || "Price token response was malformed");
    }

    const token: PriceServiceToken = {
      token: data.token,
      expiresAt,
      url: data.url.replace(/\/+$/, ""),
    };
    this.current = token;
    return token;
  }
}
